import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  TestTube,
  Copy,
  Check,
  ExternalLink,
  Eye,
  MessageSquare,
  Vote,
  FileText,
  ShoppingCart,
  MessageCircle,
  Sparkles,
  TrendingUp,
  Download,
  Share2,
} from 'lucide-react';

// Demo links that Champions can share
const demoLinks = [
  {
    id: 'voting',
    title: 'Community Voting',
    description: 'Real votes on real issues. No more Facebook poll chaos.',
    icon: Vote,
    url: 'https://montana-demo.toolboxlogic.com/voting',
    viewCount: 47,
    feedbackCount: 12,
    color: 'bg-blue-50 border-blue-200',
    iconColor: 'text-blue-600',
  },
  {
    id: 'proposals',
    title: 'Resident Proposals',
    description: 'Submit ideas, get feedback, and vote on solutions.',
    icon: FileText,
    url: 'https://montana-demo.toolboxlogic.com/proposals',
    viewCount: 34,
    feedbackCount: 8,
    color: 'bg-purple-50 border-purple-200',
    iconColor: 'text-purple-600',
  },
  {
    id: 'groupbuy',
    title: 'Group Purchasing',
    description: 'Band together for bulk discounts on services.',
    icon: ShoppingCart,
    url: 'https://montana-demo.toolboxlogic.com/group-buy',
    viewCount: 52,
    feedbackCount: 15,
    color: 'bg-green-50 border-green-200',
    iconColor: 'text-green-600',
  },
  {
    id: 'discussions',
    title: 'Discussion Forums',
    description: 'Organized community conversations with voting.',
    icon: MessageCircle,
    url: 'https://montana-demo.toolboxlogic.com/discussions',
    viewCount: 29,
    feedbackCount: 6,
    color: 'bg-orange-50 border-orange-200',
    iconColor: 'text-orange-600',
  },
];

// Social media templates
const shareTemplates = [
  {
    id: 'voting-facebook',
    platform: 'Facebook/Nextdoor',
    feature: 'Voting',
    content: `🗳️ Check out this cool new voting system for our neighborhood!

Instead of arguing in Facebook comments, we could actually VOTE on neighborhood issues. Real votes, real results.

Take a look at the demo: [LINK]

What do you think? Would you use something like this?`,
  },
  {
    id: 'groupbuy-facebook',
    platform: 'Facebook/Nextdoor',
    feature: 'Group Buying',
    content: `💰 Imagine saving 20-30% on lawn care, pest control, or snow removal...

By banding together with neighbors, we could negotiate bulk discounts with local vendors. The more of us that join, the better the deal.

Check out how it works: [LINK]

Interested? Let me know!`,
  },
  {
    id: 'proposals-nextdoor',
    platform: 'Nextdoor',
    feature: 'Proposals',
    content: `💡 Ever had an idea to improve our neighborhood but didn't know how to get it moving?

This new tool lets residents submit proposals, gather feedback, and vote on solutions. Democracy in action!

See the demo: [LINK]

What would YOU propose for our community?`,
  },
];

// Early access features
const earlyAccessFeatures = [
  {
    id: 'test-voting',
    title: 'Test Real Voting',
    description: 'Cast votes on sample neighborhood issues',
    icon: Vote,
    url: 'https://montana-demo.toolboxlogic.com/voting/test',
  },
  {
    id: 'test-proposal',
    title: 'Submit Test Proposal',
    description: 'Try creating a proposal and see the approval flow',
    icon: FileText,
    url: 'https://montana-demo.toolboxlogic.com/proposals/create',
  },
  {
    id: 'test-groupbuy',
    title: 'Browse Group Deals',
    description: 'See mock deals and how savings scale with neighbors',
    icon: ShoppingCart,
    url: 'https://montana-demo.toolboxlogic.com/group-buy/deals',
  },
];

export function FeatureDemos() {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedTemplate, setCopiedTemplate] = useState<string | null>(null);

  // Handle copy to clipboard
  const handleCopyLink = async (id: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle copy template
  const handleCopyTemplate = async (id: string, content: string, url: string) => {
    try {
      const contentWithLink = content.replace('[LINK]', url);
      await navigator.clipboard.writeText(contentWithLink);
      setCopiedTemplate(id);
      setTimeout(() => setCopiedTemplate(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Calculate total stats
  const totalViews = demoLinks.reduce((sum, demo) => sum + demo.viewCount, 0);
  const totalFeedback = demoLinks.reduce((sum, demo) => sum + demo.feedbackCount, 0);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Feature Demos & Testing</h1>
        <p className="text-muted-foreground">
          Share demo links with neighbors and test features yourself. Track engagement and collect
          feedback.
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{totalViews}</p>
                <p className="text-sm text-muted-foreground">Total Demo Views</p>
              </div>
              <Eye className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{totalFeedback}</p>
                <p className="text-sm text-muted-foreground">Feedback Received</p>
              </div>
              <MessageSquare className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{demoLinks.length}</p>
                <p className="text-sm text-muted-foreground">Active Demos</p>
              </div>
              <TestTube className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Shareable Demo Links */}
      <Card>
        <CardHeader>
          <CardTitle>Shareable Demo Links</CardTitle>
          <CardDescription>
            Copy these links to share with neighbors. They can explore features without signing up.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {demoLinks.map((demo) => {
              const Icon = demo.icon;
              const isCopied = copiedId === demo.id;

              return (
                <Card key={demo.id} className={`${demo.color} hover:shadow-md transition-shadow`}>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 bg-white rounded-lg`}>
                            <Icon className={`h-6 w-6 ${demo.iconColor}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold">{demo.title}</h3>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {demo.description}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Eye className="h-4 w-4 text-muted-foreground" />
                          <span>{demo.viewCount} views</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MessageSquare className="h-4 w-4 text-muted-foreground" />
                          <span>{demo.feedbackCount} feedback</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopyLink(demo.id, demo.url)}
                          className="flex-1"
                        >
                          {isCopied ? (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4 mr-2" />
                              Copy Link
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(demo.url, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Early Access Testing */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <CardTitle>Early Access Testing</CardTitle>
          </div>
          <CardDescription>
            Test features yourself before sharing. Your feedback shapes the final product.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {earlyAccessFeatures.map((feature) => {
              const Icon = feature.icon;

              return (
                <Card key={feature.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium">{feature.title}</h4>
                          <p className="text-sm text-muted-foreground">{feature.description}</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(feature.url, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Try It
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Social Media Templates */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-primary" />
            <CardTitle>Social Media Templates</CardTitle>
          </div>
          <CardDescription>
            Pre-written posts you can copy and share on Facebook or Nextdoor
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {shareTemplates.map((template) => {
              const isCopied = copiedTemplate === template.id;
              // Get the corresponding demo URL
              const demoUrl =
                demoLinks.find((d) => template.feature.toLowerCase() === d.id)?.url ||
                'https://montana-demo.toolboxlogic.com';

              return (
                <Card key={template.id} className="bg-muted/30">
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <div>
                          <Badge variant="outline" className="mb-1">
                            {template.platform}
                          </Badge>
                          <h4 className="font-medium">{template.feature} Post</h4>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleCopyTemplate(template.id, template.content, demoUrl)}
                        >
                          {isCopied ? (
                            <>
                              <Check className="h-4 w-4 mr-2" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4 mr-2" />
                              Copy Post
                            </>
                          )}
                        </Button>
                      </div>

                      {/* Template Preview */}
                      <div className="bg-white rounded-lg p-4 border text-sm whitespace-pre-line">
                        {template.content}
                      </div>

                      <p className="text-xs text-muted-foreground">
                        💡 The [LINK] placeholder will be replaced with the actual demo URL when you
                        copy
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Tips */}
      <Alert>
        <TrendingUp className="h-4 w-4" />
        <AlertDescription>
          <strong>Pro Tip:</strong> Share demos in small batches. Start with the voting demo (most
          popular), then follow up with group buying when neighbors show interest. Track which demos
          get the most engagement and focus there.
        </AlertDescription>
      </Alert>
    </div>
  );
}
